import React, { useRef, useState, useCallback } from 'react';
import { Document, Page } from 'react-pdf';
import type { PDFDocumentProxy } from 'pdfjs-dist';

interface IProps {
	pdfData: File | null;
}

function Reader(props: IProps) {
	const { pdfData } = props;
	const [numPages, setNumPages] = useState<number>();
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [containerWidth, setContainerWidth] = useState<number>();
	const resizeObserverRef = useRef<ResizeObserver | null>(null);
	const maxWidth = 800;
	const options = React.useMemo(() => {
		return {
			cMapUrl: '/cmaps/',
			standardFontDataUrl: '/standard_fonts/',
		};
	}, []);

	function onDocumentLoadSuccess({ numPages: nextNumPages }: PDFDocumentProxy): void {
		setNumPages(nextNumPages);
	}

	const setContainerRefAndObserve = useCallback((node: HTMLDivElement | null) => {
		containerRef.current = node;
		if (resizeObserverRef.current) {
			resizeObserverRef.current.disconnect();
			resizeObserverRef.current = null;
		}

		if (node) {
			const initialWidth = Math.min(node.clientWidth, maxWidth);
			setContainerWidth(initialWidth);

			resizeObserverRef.current = new ResizeObserver((_entries) => {
				requestAnimationFrame(() => {
					if (!containerRef.current) {
						return;
					}

					const newWidth = Math.min(containerRef.current.clientWidth, maxWidth);
					if (newWidth !== 0) { setContainerWidth(newWidth); }
				});
			});

			resizeObserverRef.current.observe(node);
		}
	}, [maxWidth]);

	return (
		<div className="d-flex flex-column" ref={setContainerRefAndObserve} aria-label="PDF viewer">
			<Document file={pdfData} options={options} onLoadSuccess={onDocumentLoadSuccess} loading={null} className={'overflow-hidden'}>
				{Array.from(new Array(numPages || 0), (_el, index) => (
					<div key={`page_${index + 1}`}>
						<Page
							pageNumber={index + 1}
							width={containerWidth || maxWidth}
							loading={null}
						/>
					</div>
				))}
			</Document>
		</div>
	);
}

export default Reader;